import { ink, newSeed, sketch } from './sketch';

export interface KnobOptions {
  label: string;
  value: number;
  defaultValue: number;
  size: 'lg' | 'sm';
  format: (v: number) => string;
  onChange: (v: number) => void;
  /** Snap to this many evenly spaced positions. */
  steps?: number;
}

const NS = 'http://www.w3.org/2000/svg';
const SWEEP = 270;

/**
 * A rotary knob. Drag vertically (Shift for fine control), scroll, or use the
 * arrow keys; double-click returns to the default. Value is 0..1.
 */
export class Knob {
  readonly el: HTMLDivElement;
  private pointer: SVGGElement;
  private arc: SVGPathElement;
  private readout: HTMLSpanElement;
  private v: number;

  constructor(private o: KnobOptions) {
    this.v = o.value;
    const el = document.createElement('div');
    el.className = `knob knob--${o.size}`;
    el.tabIndex = 0;
    el.setAttribute('role', 'slider');
    el.setAttribute('aria-label', o.label);
    el.setAttribute('aria-valuemin', '0');
    el.setAttribute('aria-valuemax', '100');

    const dial = document.createElement('div');
    dial.className = 'knob__dial';
    const seed = newSeed();
    sketch(dial, (rc, w, h) => {
      const d = Math.min(w, h) - 6;
      return [rc.circle(w / 2, h / 2, d, ink({ seed, fill: 'currentColor', fillStyle: 'hachure', hachureGap: o.size === 'lg' ? 5 : 4, fillWeight: 0.5, hachureAngle: 50 }))];
    }, 'knob__body');

    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '-50 -50 100 100');
    svg.setAttribute('class', 'knob__face');
    svg.setAttribute('aria-hidden', 'true');
    this.arc = document.createElementNS(NS, 'path');
    this.arc.setAttribute('class', 'knob__arc');
    const track = document.createElementNS(NS, 'path');
    track.setAttribute('class', 'knob__track');
    track.setAttribute('d', arcPath(0, 1));
    this.pointer = document.createElementNS(NS, 'g');
    this.pointer.setAttribute('class', 'knob__pointer');
    const line = document.createElementNS(NS, 'line');
    line.setAttribute('x1', '0');
    line.setAttribute('y1', '-12');
    line.setAttribute('x2', '0');
    line.setAttribute('y2', '-34');
    this.pointer.append(line);
    svg.append(track, this.arc, this.pointer);
    dial.append(svg);

    const label = document.createElement('span');
    label.className = 'knob__label';
    label.textContent = o.label;
    this.readout = document.createElement('span');
    this.readout.className = 'knob__value';
    el.append(dial, label, this.readout);
    this.el = el;
    this.bind(dial);
    this.render();
  }

  get value(): number {
    return this.v;
  }

  set(v: number, emit = true): void {
    let nv = Math.min(1, Math.max(0, v));
    if (this.o.steps) nv = Math.round(nv * (this.o.steps - 1)) / (this.o.steps - 1);
    if (nv === this.v) return;
    this.v = nv;
    this.render();
    if (emit) this.o.onChange(nv);
  }

  private render(): void {
    const deg = -SWEEP / 2 + this.v * SWEEP;
    this.pointer.setAttribute('transform', `rotate(${deg})`);
    this.arc.setAttribute('d', this.v > 0.001 ? arcPath(0, this.v) : '');
    const text = this.o.format(this.v);
    this.readout.textContent = text;
    this.el.setAttribute('aria-valuenow', String(Math.round(this.v * 100)));
    this.el.setAttribute('aria-valuetext', text);
  }

  private bind(dial: HTMLElement): void {
    let startY = 0;
    let startV = 0;
    const step = () => (this.o.steps ? 1 / (this.o.steps - 1) : 0.01);
    dial.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      dial.setPointerCapture(e.pointerId);
      startY = e.clientY;
      startV = this.v;
      this.el.classList.add('is-active');
      this.el.focus({ preventScroll: true });
    });
    dial.addEventListener('pointermove', (e) => {
      if (!dial.hasPointerCapture(e.pointerId)) return;
      const range = e.shiftKey ? 800 : 180;
      this.set(startV + (startY - e.clientY) / range);
    });
    const end = (e: PointerEvent) => {
      if (dial.hasPointerCapture(e.pointerId)) dial.releasePointerCapture(e.pointerId);
      this.el.classList.remove('is-active');
    };
    dial.addEventListener('pointerup', end);
    dial.addEventListener('pointercancel', end);
    dial.addEventListener('dblclick', () => this.set(this.o.defaultValue));
    dial.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const d = Math.sign(e.deltaY) * -1;
        this.set(this.v + d * Math.max(step(), e.shiftKey ? 0.002 : 0.02));
      },
      { passive: false },
    );
    this.el.addEventListener('keydown', (e) => {
      const big = e.shiftKey ? 10 : 1;
      const map: Record<string, number> = {
        ArrowUp: step() * big,
        ArrowRight: step() * big,
        ArrowDown: -step() * big,
        ArrowLeft: -step() * big,
        PageUp: 0.1,
        PageDown: -0.1,
      };
      if (e.key in map) this.set(this.v + map[e.key]);
      else if (e.key === 'Home') this.set(0);
      else if (e.key === 'End') this.set(1);
      else return;
      e.preventDefault();
      e.stopPropagation();
    });
  }
}

function arcPath(from: number, to: number): string {
  const r = 42;
  const a0 = ((-SWEEP / 2 + from * SWEEP - 90) * Math.PI) / 180;
  const a1 = ((-SWEEP / 2 + to * SWEEP - 90) * Math.PI) / 180;
  const large = (to - from) * SWEEP > 180 ? 1 : 0;
  return `M${(r * Math.cos(a0)).toFixed(2)},${(r * Math.sin(a0)).toFixed(2)} A${r},${r} 0 ${large} 1 ${(r * Math.cos(a1)).toFixed(2)},${(r * Math.sin(a1)).toFixed(2)}`;
}
